export interface NavigationLink {
  to: string;
  label: string;
  roles?: string[];
  requiresAuth?: boolean;
  orgAware?: boolean;
}

export interface NavigationSection {
  title: string;
  roles?: string[];
  links: NavigationLink[];
}

const normalizeRole = (role: string): string => role.trim().toLowerCase();

const collectStringValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map(normalizeRole);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return [normalizeRole(value)];
  }

  return [];
};

export const getUserRoles = (user: unknown): string[] => {
  if (!user || typeof user !== 'object') {
    return [];
  }

  const record = user as Record<string, unknown>;
  const appMetadata = record['app_metadata'];
  const metadataObject = appMetadata && typeof appMetadata === 'object' && !Array.isArray(appMetadata)
    ? (appMetadata as Record<string, unknown>)
    : undefined;

  const values = [
    record.roles,
    record['roles'],
    record['https://exammasters.com/roles'],
    record['https://exammasters.com/organization-roles'],
    record['https://exammasters.com/organizationRoles'],
    appMetadata,
    metadataObject?.roles,
    metadataObject?.organizationRoles,
  ];

  const roles = values.flatMap((value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>;
      return collectStringValues(nested.roles);
    }

    return collectStringValues(value);
  });

  return Array.from(new Set(roles));
};

export const hasAnyRole = (availableRoles: string[], requiredRoles?: string[]): boolean => {
  if (!requiredRoles?.length) {
    return true;
  }

  if (!availableRoles.length) {
    return false;
  }

  const normalizedAvailable = new Set(availableRoles.map(normalizeRole));
  return requiredRoles.some((role) => normalizedAvailable.has(normalizeRole(role)));
};

export const getMembershipRoles = (organizations: unknown): string[] => {
  if (!Array.isArray(organizations)) {
    return [];
  }

  return organizations.flatMap((organization) => {
    if (!organization || typeof organization !== 'object') {
      return [];
    }

    const record = organization as Record<string, unknown>;
    return collectStringValues(record.roles);
  });
};

export const mergeRoles = (...roleSets: string[][]): string[] => {
  const merged = roleSets.flatMap((roles) => roles);
  return Array.from(new Set(merged.map(normalizeRole)));
};

export const canAccessNavItem = (item: NavigationLink, isAuthenticated: boolean, roles: string[]): boolean => {
  if (item.requiresAuth && !isAuthenticated) {
    return false;
  }

  if (!item.roles?.length) {
    return true;
  }

  if (!isAuthenticated) {
    return false;
  }

  return roles.length === 0 ? true : hasAnyRole(roles, item.roles);
};

export const filterNavItems = (items: NavigationLink[], isAuthenticated: boolean, roles: string[]): NavigationLink[] => {
  return items.filter((item) => canAccessNavItem(item, isAuthenticated, roles));
};

export const filterNavSections = (sections: NavigationSection[], isAuthenticated: boolean, roles: string[]): NavigationSection[] => {
  return sections
    .map((section) => ({
      ...section,
      links: filterNavItems(section.links, isAuthenticated, roles),
    }))
    .filter((section) => section.links.length > 0 && (!section.roles?.length || hasAnyRole(roles, section.roles)));
};

export const adminDropdownLinks: NavigationLink[] = [
  { to: '/manage-organizations', label: 'Manage Organizations', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-memberships', label: 'Organization Memberships', roles: ['superadmin', 'owner'] },
  { to: '/manage-staff', label: 'Staff & Leadership', roles: ['superadmin', 'owner'] },
  { to: '/manage-teachers', label: 'Teachers', roles: ['superadmin', 'owner'] },
  { to: '/manage-students', label: 'Students', roles: ['superadmin', 'owner'] },
  { to: '/manage-teaching-assignments', label: 'Teaching Assignments', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-organization-membership-roles', label: 'Membership Roles', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-requests', label: 'Requests & Invites', roles: ['superadmin', 'owner', 'principal'] },
  { to: '/manage-platform-user-roles', label: 'Platform User Roles', roles: ['superadmin'] },
  { to: '/manage-categories', label: 'Manage Categories', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-courses', label: 'Manage Courses', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-classrooms', label: 'Manage Classrooms', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-assignments', label: 'Manage Assignments', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-schedule', label: 'Manage Schedule', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/my-schedule', label: 'My Schedule', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-exams', label: 'Manage Exams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-questions', label: 'Manage Questions', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-diagrams', label: 'Manage Diagrams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-category-diagrams', label: 'Manage Category Diagrams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-dynamic-components', label: 'Manage Dynamic Components', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-site-content', label: 'Manage Site Content', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-free-resources', label: 'Manage Free Resources', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-documents', label: 'Manage Documents', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/admin/course-workspace', label: 'Course Workspace Wizard', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/books', label: 'Books', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
];

export const settingsDropdownLinks: NavigationLink[] = [
  { to: '/analytics?pre=1', label: 'Switch Organization', requiresAuth: true },
  { to: '/my-enrollments', label: 'My Enrollments', requiresAuth: true },
  { to: '/profile', label: 'My Profile', requiresAuth: true },
  { to: '/settings', label: 'Settings', requiresAuth: true },
];

export const adminNavigationSections: NavigationSection[] = [
  {
    title: 'Overview',
    links: [
      { to: '/admin', label: 'Dashboard' },
    ],
  },
  {
    title: 'Organization',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/manage-organizations', label: 'Organizations' },
      { to: '/manage-staff', label: 'Staff & Leadership', roles: ['superadmin', 'owner'] },
      { to: '/manage-teachers', label: 'Teachers', roles: ['superadmin', 'owner'] },
      { to: '/manage-students', label: 'Students', roles: ['superadmin', 'owner'] },
      { to: '/manage-teaching-assignments', label: 'Teaching Assignments', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
      { to: '/manage-organization-requests', label: 'Requests & Invites' },
    ],
  },
  {
    title: 'Learning Content',
    roles: ['superadmin', 'owner', 'principal', 'teacher'],
    links: [
      { to: '/manage-courses', label: 'Courses' },
      { to: '/manage-categories-v2', label: 'Categories V2' },
      { to: '/manage-questions', label: 'Questions' },
      { to: '/manage-classrooms', label: 'Classrooms' },
      { to: '/manage-site-content', label: 'Site Content' },
    ],
  },
  {
    title: 'Assessments',
    roles: ['superadmin', 'owner', 'principal', 'teacher'],
    links: [
      { to: '/manage-assignments', label: 'Assignments' },
      { to: '/manage-schedule', label: 'Manage Schedule' },
      { to: '/my-schedule', label: 'My Schedule' },
      { to: '/manage-exams', label: 'Exams' },
    ],
  },
  {
    title: 'Super Admin',
    roles: ['superadmin'],
    links: [
      { to: '/manage-organization-memberships', label: 'Organization Memberships' },
      { to: '/manage-organization-membership-roles', label: 'Membership Roles' },
      { to: '/manage-platform-user-roles', label: 'Platform User Roles' },
      { to: '/manage-categories', label: 'Categories' },
      { to: '/admin/course-workspace', label: 'Course Workspace Wizard' },
    ],
  },
  {
    title: 'Resources',
    roles: ['superadmin'],
    links: [
      { to: '/manage-free-resources', label: 'Free Resources' },
      { to: '/manage-documents', label: 'Documents' },
      { to: '/books', label: 'Books' },
      { to: '/manage-diagrams', label: 'Diagrams' },
      { to: '/manage-category-diagrams', label: 'Category Diagrams' },
      { to: '/manage-dynamic-components', label: 'Dynamic Components' },
    ],
  },
];
